import React, { FC, useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, notification, message, Spin } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import skyBg from '../assets/images/background/SkyWithClouds.png';
import ArrowLeft from '../components/svg/ArrowLeft';
import Calendar from '../components/svg/Calendar';
import PenPaper from '../components/svg/PenPaper';
import Paper from '../components/svg/Paper';
import TrashBin from '../components/svg/TrashBin';
import ArrowRight from '../components/svg/ArrowRight';
import LogOutIcon from '../components/svg/LogOut';
import treeImage from '../assets/images/background/TreeWithBird.png';
import CurvDown from '../components/svg/CurvDown';
import ReloadIcon from '../components/svg/Reload';
import RefreshButton from '../components/RefreshButton';
import rocksLeft from '../assets/images/background/RocksWithBird.png';
import rocksRight from '../assets/images/background/RocksWithGrass2.png';
import { fullname, role, email } from '../utils/data';
import { getAuthToken } from '../utils/functions';
import { CaseNumbersUrl, CaseDetailsUrl, BaseUrl } from '../utils/network';
import { getCaseSections, CaseSectionConfig } from '../utils/caseSections';

// Import section components
import GeneralInformationSection from '../components/caseSections/GeneralInformation';
import HandicapSection from '../components/caseSections/Handicap';
import AccommodationSection from '../components/caseSections/Accommodation';
import AgeSection from '../components/caseSections/Age';
import IssuesSection from '../components/caseSections/Issues';
import LossesSection from '../components/caseSections/Losses';
import HarassmentSection from '../components/caseSections/Harassment';
import PositionInformationSection from '../components/caseSections/PositionInformation';
import SelecteesInformationSection from '../components/caseSections/SelecteesInformation';
import SexualHarassmentSection from '../components/caseSections/SexualHarassment';
import SexualHarassmentActionsSection from '../components/caseSections/SexualHarassmentActions';
import TerminationSection from '../components/caseSections/Termination';
import CoworkersInformationSection from '../components/caseSections/CoworkersInformation';
import ResponsesSection from '../components/caseSections/Responses';
import AnalysisResultsModal from '../components/AnalysisResultsModal';
import Header from '../components/Header';

// Reusable Form Components
const FormInput = ({ label, required, placeholder, value, disabled, onChange }: any) => (
  <div className="flex flex-col gap-[11px] md:gap-[16px] w-full">
    {label && (
      <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] font-medium text-[#1F2A37]">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
    )}
    <input 
      type="text"
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className={`w-full bg-[#F3F4F6] text-[#1F2A37] border-none rounded-[8px] px-4 py-3 text-[14px] font-['DM_Sans',sans-serif] placeholder-[#9CA3AF] outline-none ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
    />
  </div>
);

const FormSelect = ({ label, required, placeholder, value, disabled, onChange, options }: any) => (
  <div className="flex flex-col gap-[11px] md:gap-[16px] w-full relative">
    {label && (
      <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] font-medium text-[#1F2A37]">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
    )}
    <div className="relative">
      <select 
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`w-full bg-[#F3F4F6] text-[#1F2A37] appearance-none border-none rounded-[8px] px-4 py-3 text-[14px] ${value ? "text-[#1F2A37]" : "text-[#9CA3AF]"} font-['DM_Sans',sans-serif] outline-none ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
      >
        <option value="" disabled hidden>{placeholder}</option>
        {options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <CurvDown className="pt-[10px] md:pt-[14px]" />
      </div>
    </div>
  </div>
);

const FormDate = ({ label, required, placeholder, value, disabled, onChange }: any) => (
  <div className="flex flex-col gap-[11px] md:gap-[16px] w-full">
    {label && (
      <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] font-medium text-[#1F2A37]">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
    )}
    <div className="relative">
      <input 
        type="text" 
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full bg-[#F3F4F6] text-[#1F2A37] border-none rounded-[8px] px-4 py-3 text-[14px] font-['DM_Sans',sans-serif] placeholder-[#9CA3AF] outline-none ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
      />
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <Calendar />
      </div>
    </div>
  </div>
);


// Sections
const GeneralInformation = () => (
  <div className="flex flex-col w-full h-full pb-8">
    <div className="flex items-center gap-4 mb-[28px] lg:mb-[37px]">
      <h2 className="font-['Lexend',sans-serif] text-[14px] lg:text-[20px] font-medium text-[#1F2A37] m-0 leading-[29px]">General Information</h2>
      <div className="w-[30px] lg:w-[46px] h-0 border-t-[1.5px] lg:border-t-[2px] border-[#6FB6F2]  "></div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-[37px]">
      <FormInput label="Case Number" value="172" disabled className="mb-0"/>
      <FormSelect label="Case Type" required placeholder="Select" options={['Accommodation', 'Different Treatment', 'Harassment', 'Nonselection', 'Sexual Harassment', 'Termination']} value="Accommodation" />
      <FormSelect label="Discrimination Basis" required placeholder="Discrimination Basis" value="" options={['Age', 'Color', 'Mental/Physical Handicap', 'National Origin', 'Race', 'Religion', 'Reprisal', 'Sex']} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-[62px]">
      <FormDate label="Date Discrimination Occurred" placeholder="Select Date" />
      <FormDate label="Date of Interview" placeholder="Select Date" />
    </div>

    <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] mb-4 mt-4 leading-[29px]">Personal Information</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
      <FormInput label="Last Name" placeholder="Last Name" />
      <FormInput label="First Name" placeholder="First Name" />
      <FormInput label="Middle Name" placeholder="Middle Name" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
      <FormInput label="Your Basis is" placeholder="Enter here" />
    </div>

    <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] mb-4 mt-4 leading-[29px]">Accused's Information</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
      <FormInput label="Company Name" placeholder="Company Name" />
      <FormInput label="Last Name" placeholder="Last Name" />
      <FormInput label="First Name" placeholder="First Name" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <FormInput label="Accused Basis is" placeholder="Enter here" />
    </div>
  </div>
);

const Handicap = () => (
  <div className="flex flex-col w-full h-full pb-8">
    <div className="flex items-center gap-4 mb-6">
      <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[20px] font-semibold text-[#1F2A37] m-0">Handicap</h2>
      <div className="h-[2px] w-[30px] bg-[#7BA8CE]"></div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
      <FormSelect label="What Type of Handicap Do You Have?" placeholder="Select" options={['Physical', 'Mental']} />
      <FormSelect label="Your disability affects your" placeholder="Select" options={['Mobility', 'Vision', 'Hearing']} />
      <FormSelect label="Are you generally considered handicapped by other employees?" placeholder="Select" options={['Yes', 'No']} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
      <FormSelect label="Was the accused aware of your handicap?" placeholder="Select" options={['Yes', 'No']} />
      <FormDate label="Date the accused learned of your handicap" placeholder="Select Date" />
      <FormSelect label="How did he learn of it?" placeholder="Select" options={['Observation', 'Told by me', 'Other']} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <FormSelect label="Is the accused handicapped?" placeholder="Select" options={['Yes', 'No']} />
      <FormSelect label="What type of handicap does accused have?" placeholder="Select" options={['Physical', 'Mental']} />
      <FormInput label="What is the accused's Handicap" placeholder="Enter here" />
    </div>
  </div>
);

const Accommodation = () => (
  <div className="flex flex-col w-full h-full pb-8">
    <div className="flex items-center gap-4 mb-6">
      <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[20px] font-semibold text-[#1F2A37] m-0">Accommodation</h2>
      <div className="h-[2px] w-[30px] bg-[#7BA8CE]"></div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-3xl">
      <FormSelect label="What did you request?" placeholder="Select" options={['Equipment', 'Schedule Change']} />
      <FormInput label="What was the date of this request?" placeholder="Enter here" />
    </div>
  </div>
);

const CoworkerInformation = ({ isViewMode }: { isViewMode?: boolean }) => {
  const [rows, setRows] = useState([1, 2]);

  return (
    <div className="flex flex-col w-full h-full pb-8">
      <div className="flex items-center gap-4 mb-6">
        <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[20px] font-semibold text-[#1F2A37] m-0">Coworker Informations</h2>
        <div className="h-[2px] w-[30px] bg-[#7BA8CE]"></div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3  gap-6 mb-8 ">
        <FormSelect label="Were Other employee treated the same way as you?" placeholder="Select" options={['Yes', 'No']} />
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="font-['Lexend',sans-serif] text-[16px] font-semibold text-[#1F2A37] m-0">Coworkers Information</h3>
        <button 
          className="text-[#4682B4] bg-transparent border-none font-medium flex items-center gap-2 cursor-pointer hover:opacity-80 font-['Lexend',sans-serif]" 
          onClick={() => setRows([...rows, rows.length + 1])}
          disabled={isViewMode}
        >
          <span className="text-[18px] leading-none">+</span> Add More
        </button>
      </div>

      <div className="flex flex-col gap-4 mb-8">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-col md:flex-row items-end gap-4 w-full">
            <div className="flex items-center gap-4 w-full md:w-1/3">
              <span className="text-[#9CA3AF] font-['DM_Sans',sans-serif] font-medium w-6 shrink-0">{(i + 1).toString().padStart(2, '0')}</span>
              <FormInput label={i === 0 ? "Names" : ""} placeholder="Names" />
            </div>
            <div className="flex items-center gap-4 w-full md:w-1/3">
              <div className="w-full">
                <FormSelect label={i === 0 ? "Basis" : ""} placeholder="Select" options={['Base 1', 'Base 2']} />
              </div>
              <button className="bg-transparent border-none cursor-pointer mt-[22px] hover:opacity-70 p-2">
                <TrashBin />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <FormSelect label="Did your coworker work for the same supervisor?" placeholder="Select" options={['Yes', 'No']} />
        <FormInput label="Different Supervisor's name" placeholder="Enter here" />
      </div>
    </div>
  );
};

const Responses = () => (
  <div className="flex flex-col w-full h-full pb-8">
    <div className="flex items-center gap-4 mb-6">
      <h2 className="font-['Lexend',sans-serif] text-[18px] md:text-[20px] font-semibold text-[#1F2A37] m-0">Responses</h2>
      <div className="h-[2px] w-[30px] bg-[#7BA8CE]"></div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
      <FormSelect label="What reason were you given for your employer's actions?" placeholder="Select" options={['Reason 1', 'Reason 2']} />
      <FormSelect label="Your Response" placeholder="Select" options={['Response 1', 'Response 2']} />
      <FormSelect label="Do you have documented proof?" placeholder="Select" options={['Yes', 'No']} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      <FormSelect label="Do you have any witnesses that will testify under oath?" placeholder="Select" options={['Yes', 'No']} />
    </div>
  </div>
);


// Main Component
export default function InterviewWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [caseType, setCaseType] = useState<string | undefined>();
  const [discriminationBasis, setDiscriminationBasis] = useState<string | undefined>();
  const [sections, setSections] = useState<CaseSectionConfig>(getCaseSections(undefined, undefined));
  const [nextCaseNo, setNextCaseNo] = useState<number | null>(null);
  const [fetchingCaseNo, setFetchingCaseNo] = useState(true);
  const [createdCaseNumberId, setCreatedCaseNumberId] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [caseResponse, setCaseResponse] = useState<any>(null);
  const caseId = searchParams.get('caseId');
  const isViewOnly = searchParams.get('view') === 'true';
  const isViewMode = !!caseId;
  const [loadingCase, setLoadingCase] = useState(false);

  const currentUser = {
    role: localStorage.getItem(role) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(email) || ''
  };
  const isAdmin = currentUser.role.toLowerCase() === 'admin';

  // Update sections when case type or basis changes
  useEffect(() => {
    setSections(getCaseSections(caseType, discriminationBasis));
  }, [caseType, discriminationBasis]);

  // Load existing case or fetch next case number
  useEffect(() => {
    const loadData = async () => {
      const headers = getAuthToken();
      if (!headers) return;

      if (isViewMode && caseId) {
        try {
          setLoadingCase(true);
          const response = await axios.get(`${CaseDetailsUrl}${caseId}/`, headers);
          const caseData = response.data;
          const convertDates = (obj: any) => {
            if (!obj) return;
            const dateFields = [
              'date_discrimination_occurred', 'date_of_interview',
              'date_accused_learned', 'date_of_request', 'your_dob', 'accused_dob',
              'dob', 'date', 'date_notified_supervisor', 'date_of_action', 'date_not_selected'
            ];
            for (const key in obj) {
              if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (dateFields.includes(key) && obj[key] && typeof obj[key] === 'string') {
                  obj[key] = dayjs(obj[key]);
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                  convertDates(obj[key]);
                }
              }
            }
          };
          convertDates(caseData);
          form.setFieldsValue(caseData);
          setCaseType(caseData.case_type);
          setDiscriminationBasis(caseData.discrimination_basis);
          setNextCaseNo(caseData.case_no || caseData.case_number_detail?.case_no);
          setCaseResponse(caseData);
        } catch (error) {
          notification.error({ message: 'Error', description: 'Failed to load case data.' });
        } finally {
          setLoadingCase(false);
          setFetchingCaseNo(false);
        }
      } else {
        try {
          const response = await axios.get(`${CaseNumbersUrl}next/`, headers);
          setNextCaseNo(response.data.next_case_no);
          form.setFieldsValue({ case_no: response.data.next_case_no });
        } catch (error) {
          notification.error({ message: 'Error', description: 'Failed to fetch next case number.' });
        } finally {
          setFetchingCaseNo(false);
        }
      }
    };
    loadData();
  }, [form, isViewMode, caseId]);

  // Cleanup refs
  const createdCaseNumberIdRef = useRef<number | null>(null);
  const isSubmittedRef = useRef<boolean>(false);
  useEffect(() => { createdCaseNumberIdRef.current = createdCaseNumberId; }, [createdCaseNumberId]);
  useEffect(() => { isSubmittedRef.current = isSubmitted; }, [isSubmitted]);
  useEffect(() => {
    return () => {
      if (createdCaseNumberIdRef.current && !isSubmittedRef.current) {
        const cleanup = async () => {
          try {
            const headers = getAuthToken();
            if (headers) await axios.delete(`${CaseNumbersUrl}${createdCaseNumberIdRef.current}/`, headers);
          } catch (error) { console.error('Error cleaning up case number:', error); }
        };
        cleanup();
      }
    };
  }, []);

  // Watch form values
  const handleFormValuesChange = (changedValues: any) => {
    if (changedValues.case_type !== undefined) {
      setCaseType(changedValues.case_type);
    }
    if (changedValues.discrimination_basis !== undefined) setDiscriminationBasis(changedValues.discrimination_basis);
  };

  // Build dynamic steps
  interface StepConfig { key: string; title: string; component: React.ReactNode; }
  const buildSteps = (): StepConfig[] => {
    const s: StepConfig[] = [];
    if (sections.showGeneralInformation) s.push({ key: 'general', title: 'General Information', component: <GeneralInformationSection form={form} discriminationBasis={discriminationBasis} caseNo={nextCaseNo} loadingCaseNo={fetchingCaseNo} /> });
    if (sections.showHandicap) s.push({ key: 'handicap', title: 'Handicap', component: <HandicapSection form={form} /> });
    if (sections.showAccommodation) s.push({ key: 'accommodation', title: 'Accommodation', component: <AccommodationSection form={form} /> });
    if (sections.showAge) s.push({ key: 'age', title: 'Age Information', component: <AgeSection form={form} /> });
    if (sections.showSexualHarassment) s.push({ key: 'sexual_harassment', title: 'Sexual Harassment', component: <SexualHarassmentSection form={form} /> });
    if (sections.showSexualHarassmentActions) s.push({ key: 'sexual_harassment_actions', title: 'Sexual Harrassment Actions', component: <SexualHarassmentActionsSection form={form} /> });
    if (sections.showHarassment) s.push({ key: 'harassment', title: 'Harassment', component: <HarassmentSection form={form} /> });
    if (sections.showIssues) s.push({ key: 'issues', title: 'Issues', component: <IssuesSection form={form} /> });
    if (sections.showPositionInformation) s.push({ key: 'position', title: 'Position Information', component: <PositionInformationSection form={form} /> });
    if (sections.showSelecteesInformation) s.push({ key: 'selectees', title: 'Selectees Information', component: <SelecteesInformationSection form={form} /> });
    if (sections.showLosses) s.push({ key: 'losses', title: 'Losses', component: <LossesSection form={form} caseType={caseType} discriminationBasis={discriminationBasis} /> });
    if (sections.showTermination) s.push({ key: 'termination', title: 'Termination', component: <TerminationSection form={form} /> });
    if (sections.showCoworkersInformation) s.push({ key: 'coworkers', title: 'Coworker Information', component: <CoworkersInformationSection form={form} caseType={caseType} /> });
    if (sections.showResponses) s.push({ key: 'responses', title: 'Responses', component: <ResponsesSection form={form} /> });
    return s;
  };

  const steps = buildSteps();

  // Handle step=last parameter for View mode
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (isViewMode && !loadingCase && stepParam === 'last' && steps.length > 0) {
      setCurrentStep(steps.length - 1);
    }
  }, [isViewMode, loadingCase, searchParams, steps.length]);

  const isLastStep = currentStep === steps.length - 1;

  const handleNext = async () => {
    if (isViewOnly) {
      if (!isLastStep) { setCurrentStep(currentStep + 1); window.scrollTo(0, 0); }
      return;
    }
    try {
      await form.validateFields();
      if (!isLastStep) { setCurrentStep(currentStep + 1); window.scrollTo(0, 0); }
    } catch { message.error('Please fill in all required fields before proceeding.'); }
  };

  const handlePrevious = () => {
    if (currentStep > 0) { setCurrentStep(currentStep - 1); window.scrollTo(0, 0); }
  };

  const handleCancel = async () => {
    if (createdCaseNumberId && !isSubmitted) {
      try {
        const headers = getAuthToken();
        if (headers) await axios.delete(`${CaseNumbersUrl}${createdCaseNumberId}/`, headers);
      } catch (error) { console.error('Error cleaning up:', error); }
    }
    navigate('/');
  };

  const handleViewFinish = async () => {
    if (!caseResponse?.pdf_url) {
      message.error('PDF report is not available');
      navigate('/');
      return;
    }

    try {
      const baseUrl = BaseUrl.replace('/api/', ''); 
      const fullUrl = caseResponse.pdf_url.startsWith('http') ? caseResponse.pdf_url : `${baseUrl}${caseResponse.pdf_url}`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('employeepro_token') || localStorage.getItem('token')}` || '',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = caseResponse.pdf_filename || `case_${caseResponse.case_no}_analysis.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      message.success('PDF report downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to download PDF report.');
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const headers = getAuthToken();
      if (!headers) throw new Error('Authentication required');
      const values = form.getFieldsValue(true);

      // Create case number
      const createRes = await axios.post(CaseNumbersUrl, {}, headers);
      const caseNumberId = createRes.data.id;
      const generatedCaseNo = createRes.data.case_no;
      setCreatedCaseNumberId(caseNumberId);
      form.setFieldsValue({ case_no: generatedCaseNo });
      setNextCaseNo(generatedCaseNo);

      // Prepare data
      const data: any = {
        case_number: caseNumberId, case_type: values.case_type, discrimination_basis: values.discrimination_basis,
        date_discrimination_occurred: values.date_discrimination_occurred ? (typeof values.date_discrimination_occurred === 'string' ? values.date_discrimination_occurred : values.date_discrimination_occurred.format('YYYY-MM-DD')) : null,
        date_of_interview: values.date_of_interview ? (typeof values.date_of_interview === 'string' ? values.date_of_interview : values.date_of_interview.format('YYYY-MM-DD')) : null,
        last_name: values.last_name, first_name: values.first_name, middle: values.middle,
        company_name: values.company_name, accused_last_name: values.accused_last_name, accused_first_name: values.accused_first_name,
      };

      // Add basis fields
      ['your_age','your_color','your_handicap','your_national_origin','your_race','your_religion','your_reprisal','your_sex',
       'accused_age','accused_color','accused_handicap','accused_national_origin','accused_race','accused_religion','accused_reprisal','accused_sex'
      ].forEach(f => { if (values[f]) data[f] = values[f]; });

      // Add nested model data
      const nestedMap: Record<string, string> = {
        showHandicap: 'handicap_info', showAccommodation: 'accommodation_info', showAge: 'age_info',
        showIssues: 'issues_info', showLosses: 'losses_info', showHarassment: 'harassment_info',
        showPositionInformation: 'position_info', showSelecteesInformation: 'selectees_info',
        showSexualHarassment: 'sexual_harassment_info', showTermination: 'termination_info',
        showCoworkersInformation: 'coworkers_info', showResponses: 'responses_info',
      };
      Object.entries(nestedMap).forEach(([sectionKey, fieldKey]) => {
        if ((sections as any)[sectionKey] && values[fieldKey]) data[fieldKey] = values[fieldKey];
      });

      const response = await axios.post(CaseDetailsUrl, data, headers);
      setCaseResponse(response.data);
      setIsSubmitted(true);
      setCreatedCaseNumberId(null);
      notification.success({ message: 'Case Created Successfully', description: `Case #${generatedCaseNo} has been created and analyzed.` });
      setShowResultsModal(true);
    } catch (error: any) {
      let errorMessage = 'Failed to create case';
      let errorDescription = 'An unexpected error occurred.';
      if (error.response?.status === 400 && error.response.data) {
        errorDescription = Object.entries(error.response.data).map(([k, v]: [string, any]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
      } else if (error.response?.data?.detail) { errorDescription = error.response.data.detail; }
      notification.error({ message: errorMessage, description: errorDescription, duration: 6 });
    } finally { setLoading(false); }
  };

  return (
    <Spin spinning={loading || loadingCase} tip={loadingCase ? "Loading case..." : "Creating case..."} size="large">
    <Form 
      form={form} 
      onValuesChange={handleFormValuesChange} 
      layout="vertical"
      disabled={isViewOnly}
    >
    <div className="relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['DM_Sans',sans-serif]">
      {/* Background Images */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover md:min-h-[220px]" />
      </div>

      {/* Header */}
      <Header currentUser={currentUser} showAdmin={false} />


      {/* Main Content */}
      <main className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12"> 
        <style dangerouslySetInnerHTML={{__html: `
          .no-scrollbar::-webkit-scrollbar { display: none; }
          
          /* Global Wizard Form Styling */
          .wizard-container .ant-form-item {
            margin-bottom: 0px !important;
          }
          .wizard-container .ant-form-item-label {
            display: flex !important;
            align-items: flex-end !important;
            min-height: 30px !important;
            padding-bottom: 0px !important;
          }
          .wizard-container .ant-form-item-label > label {
            font-family: 'Lexend', sans-serif !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            color: #1F2A37 !important;
            height: auto !important;
            margin-bottom: 11px !important;
          }
          @media (min-width: 768px) {
            .wizard-container .ant-form-item-label {
              min-height: 60px !important;
            }
            .wizard-container .ant-form-item-label > label {
              font-size: 16px !important;
              margin-bottom: 8px !important;
            }
          }
          /* Remove default Ant Design padding-bottom from vertical labels */
          .wizard-container .ant-form-item-vertical .ant-form-item-label {
            padding-bottom: 0px !important;
          }
          /* Custom required asterisk styling */
          .wizard-container .ant-form-item-label > label.ant-form-item-required::before {
            display: none !important;
          }
          .wizard-container .ant-form-item-label > label.ant-form-item-required::after {
            content: "*" !important;
            display: inline-block !important;
            margin-left: 4px !important;
            color: #ff4d4f !important;
            font-size: 14px !important;
            font-family: inherit !important;
            visibility: visible !important;
            line-height: 1 !important;
          }
          .wizard-container .ant-input,
          .wizard-container .ant-input-number,
          .wizard-container .ant-select-selector,
          .wizard-container .ant-picker {
            background-color: #032B440D !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 4px 16px !important;
            font-size: 14px !important;
            font-family: 'DM_Sans', sans-serif !important;
            color: #1F2A37 !important;
            box-shadow: none !important;
            min-height: 40px !important;
            height: 40px !important;
            width: 100% !important;
            display: flex !important;
            align-items: center !important;
          }
          @media (max-width: 767px) {
            .wizard-container .ant-input,
            .wizard-container .ant-input-number,
            .wizard-container .ant-select-selector,
            .wizard-container .ant-picker {
              min-height: 40px !important;
              height: 40px !important;
              max-height: 40px !important;
              padding: 0 16px !important;
            }
            .wizard-container .ant-select-selection-item,
            .wizard-container .ant-select-selection-placeholder {
              line-height: 38px !important; 
            }
          }
          @media (min-width: 768px) {
            .wizard-container .ant-input,
            .wizard-container .ant-input-number,
            .wizard-container .ant-select-selector,
            .wizard-container .ant-picker {
              min-height: 47px !important;
              height: 47px !important;
              padding: 8px 16px !important;
            }
            .wizard-container .ant-select-selection-item,
            .wizard-container .ant-select-selection-placeholder {
              line-height: 47px !important; 
            }
          }
          /* Custom Dropdown Arrow Scaling */
          .wizard-container .ant-select-arrow svg {
            width: 7px !important;
            height: 3px !important;
          }
          @media (min-width: 768px) {
            .wizard-container .ant-select-arrow svg {
              width: 11px !important;
              height: 6px !important;
            }
          }
          .wizard-container .ant-input-number-input {
            height: 29px !important;
            padding: 0 !important;
          }
          .wizard-container .ant-picker-suffix svg,
          .wizard-container .ant-select-arrow svg {
            width: 14px !important;
            height: auto !important;
          }
          @media (min-width: 768px) {
            .wizard-container .ant-picker-suffix svg,
            .wizard-container .ant-select-arrow svg {
              width: 18px !important;
              height: auto !important;
            }
          }
          .wizard-container .ant-input::placeholder,
          .wizard-container .ant-picker-input > input::placeholder,
          .wizard-container .ant-select-selection-placeholder {
            color: #9CA3AF !important;
            font-size: 10px !important;
          }
          @media (min-width: 768px) {
            .wizard-container .ant-input::placeholder,
            .wizard-container .ant-picker-input > input::placeholder,
            .wizard-container .ant-select-selection-placeholder {
              font-size: 14px !important;
            }
          }

          /* Disabled states styling */
          .wizard-container .ant-form-item:has(.ant-input-disabled, .ant-select-disabled, .ant-picker-disabled) .ant-form-item-label > label {
            opacity: 1 !important;
          }

          .wizard-container .ant-input-disabled,
          .wizard-container .ant-input-number-disabled,
          .wizard-container .ant-select-disabled .ant-select-selector,
          .wizard-container .ant-picker-disabled {
            background-color: #032B4405 !important;
            color: #1F2A37 !important;
            border: none !important;
          }

          .wizard-container .ant-input-disabled::placeholder,
          .wizard-container .ant-select-disabled .ant-select-selection-placeholder {
            color: #1F2A374D !important;
          }

          .wizard-container .ant-select-disabled .ant-select-arrow,
          .wizard-container .ant-picker-disabled .ant-picker-suffix {
            opacity: 1 !important;
          }
          .wizard-container .ant-input-number-handler-wrap {
            display: none !important;
          }
          .wizard-container .ant-picker-suffix, .wizard-container .ant-select-arrow {
            color: #1F2A37 !important;
          }
          .wizard-container .ant-form-item-explain-error {
            font-family: 'DM_Sans', sans-serif !important;
            font-size: 12px !important;
            margin-top: 14px !important;
            color: #ef4444 !important;
          }
          .wizard-container .ant-form-item-control-input {
          
            overflow: visible !important;
          }
          .wizard-container .ant-form-item-control {
             overflow: visible !important;
          }
          .wizard-container .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
             height: 45px !important;
          }
        `}} />
        <div className="wizard-container w-full h-full flex flex-col">

        {/* Top Stepper Card Wrapper */}
        <div className="self-start w-full md:w-full pb-1">
          <div 
            className="premium-card white w-full p-5 rounded-[17px] md:p-6 mb-[26px] md:mb-[39px] flex flex-col gap-3 md:gap-6 overflow-hidden">
            <div className="flex items-center gap-3">
              <div className="hidden md:block"><PenPaper /></div>
              <div className="block md:hidden"><Paper /></div>
              <span className="font-['Lexend',sans-serif] text-[14px] md:text-[20px] font-medium leading-[29px] text-[#1F2A37]">
                {fetchingCaseNo ? 'Loading...' : `Case Number - ${nextCaseNo || 'N/A'}`}
              </span>
            </div>

            <div className="w-full mt-2 relative overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex items-start w-full min-w-[600px] md:min-w-0 relative">
                {steps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isPast = index < currentStep;
                  const isLast = index === steps.length - 1;
                  
                  return (
                    <div key={index} className="flex-1 min-w-[150px] md:min-w-0 relative flex flex-col items-center">
                      {/* Connecting Line (between steps) */}
                      {!isLast && (
                        <div 
                          className="absolute top-[6px] md:top-[10px] left-[50%] right-[-50%] h-[2px] z-0 flex items-center"
                        >
                          <div className={`w-full border-t-2 mx-[13px] md:mx-[21px] ${index < currentStep ? 'border-solid border-[#4682B4]' : 'border-dashed border-[#a3c9e8]'}`} 
                          />
                        </div>
                      )}

                      {/* Step Indicator and Title */}
                      <div 
                        className="flex flex-col items-center justify-center gap-2 z-10 relative" 
                      >
                        <div className={`w-[14px] h-[14px] md:w-[21px] md:h-[21px] rounded-full flex items-center justify-center font-['Lexend',sans-serif] text-[8px] md:text-[12px] font-medium leading-[21px] md:leading-normal transition-all duration-300 ${isActive || isPast ? 'bg-[#4682B4] text-white' : 'bg-white text-[#1F2A37] border border-[#4682B4]'}`}>
                          {index + 1}
                        </div>
                        <span className={`font-['Lexend',sans-serif] text-[10px] lg:text-[14px] text-nowrap ${isActive || isPast ? 'font-normal md:font-medium text-[#1F2A37]' : 'font-light text-[#555D67]'} leading-[21px] text-center align-middle px-2`}>
                          {step.title}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Content Card - renders actual Ant Design section components */}
        <div 
          className="premium-card blue-tint white w-full p-6 pt-[26px] md:pt-[37px] px-[21px] md:px-[41px] pb-[45px] md:pb-[59px] mb-6 flex flex-col"
        >
          <div className="w-full flex-grow">
            {steps[currentStep]?.component}
          </div>

          <div className="w-full flex justify-between items-center md:mt-8 pt-[10px] border-t-0">
            <button className="px-5 py-[7px] md:px-[37px] md:py-[12px] rounded-full bg-[#4682B42B] hover:bg-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-medium text-[12px] md:text-[14px] border-none cursor-pointer transition-colors" onClick={handleCancel} disabled={false}>
              Cancel
            </button>

            <div className="flex gap-3 md:gap-4">
              {currentStep > 0 && (
                <button 
                  className="pl-[4px] pr-[12px] py-[3px] md:pl-[9px] md:pr-[21px] md:py-[8px] h-[32px] md:h-[46px]  rounded-full bg-white border border-[#4682B4] text-[#151313] font-['Lexend',sans-serif] text-[12px] md:text-[16px] flex items-center gap-[4px] md:gap-[16px] cursor-pointer transition-colors hover:bg-[#f8FAFC]"
                  onClick={handlePrevious}
                  disabled={false}
                >
                  <div className="scale-[0.8] md:scale-100 flex items-center justify-center origin-center"><ArrowLeft /></div>
                  Back
                </button>
              )}
              
              {isLastStep && isViewOnly && (
                <button 
                  className="pl-[16px] pr-[16px] py-[4px] md:pl-[24px] md:pr-[24px] md:py-[8px] rounded-full bg-white border border-[#4682B4] text-[#4682B4] font-['Lexend',sans-serif] font-medium text-[12px] md:text-[16px] flex items-center justify-center gap-[10px] md:gap-[16px] cursor-pointer transition-colors hover:bg-[#f8FAFC]"
                  onClick={handleViewFinish}
                  disabled={false}
                >
                  Download
                </button>
              )}
              
              <button 
                className="pl-[4px] pr-[16px] py-[4px] md:pl-[9px] md:pr-[36px] md:py-[8px] rounded-full bg-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[16px] border-none cursor-pointer transition-colors"
                onClick={isLastStep ? (isViewOnly ? () => navigate('/') : onSubmit) : handleNext}
                disabled={false}
              > <div className="w-[20px] h-[20px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                  <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                </div>
                {isLastStep ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>
        </div>


      </main>

      {/* Bottom Assets */}
      <div className="relative w-full pointer-events-none z-[1] flex justify-between items-end mt-auto">
        <img src={rocksLeft} alt="" className="w-[190px] md:w-[220px] h-auto object-contain object-left-bottom" />
        <img src={rocksRight} alt="" className="w-[120px] md:w-[150px] h-auto object-contain object-right-bottom" />
      </div>
    </div>
    </Form>


    {/* Analysis Results Modal */}
    {caseResponse && (
      <AnalysisResultsModal
        visible={showResultsModal}
        onClose={() => {
          setShowResultsModal(false);
          form.resetFields();
          setCaseType(undefined);
          setDiscriminationBasis(undefined);
          setSections(getCaseSections(undefined, undefined));
          setCreatedCaseNumberId(null);
          setCaseResponse(null);
          setCurrentStep(0);
          navigate('/');
        }}
        analysisResults={{
          likelihood: (caseResponse.likelihood || 'Medium') as 'High' | 'Moderate' | 'Low',
          confidence_score: caseResponse.weighted_score ? parseFloat(caseResponse.weighted_score) / 5 : 0.4,
          case_ref: `Case #${caseResponse.case_no || caseResponse.case_number_detail?.case_no || 'N/A'}`,
          reasoning: `Based on the analysis, this case has a ${caseResponse.likelihood || 'Medium'} likelihood index with a weighted score of ${caseResponse.weighted_score || 'N/A'}.`
        }}
        caseNumber={caseResponse.case_no || caseResponse.case_number_detail?.case_no || 0}
        pdfUrl={caseResponse.pdf_url}
        pdfFilename={caseResponse.pdf_filename}
      />
    )}
    </Spin>
  );
}

